import type { AppThunk } from '@app/store/store';
import { authenticationApiSlice } from '@features/authentication/api/authentication.api';
import { skipBroadcast } from '@features/authentication/utils/authBroadcast';
import { getEmailFromAccessToken } from '@features/authentication/utils/authentication.utils';
import { clearActivity } from '@features/authentication/utils/inactivity';
import { cancelTrackedReservation } from '@features/token-management/utils/reservation-registry';
import { userManagementApiSlice } from '@features/user-management/api/user-management.api';
import { bootstrapRefreshToken, cancelInFlightRefresh } from '@shared/utils/access-token-refresh';
import { RefreshStatus } from '@shared/utils/access-token-refresh.types';
import { resetAllApiState } from '@shared/utils/login.utils';
import { isUnreachableError } from '@shared/utils/network-error.utils';

import { logout, selectIsAuthenticated, setBootstrapStatus, setCredentials } from './authentication.slice';

/**
 * Web Lock to prevent a "Logout Stampede".
 * When the inactivity timer hits zero, ALL open tabs try to log out at the exact same millisecond.
 * This lock ensures only ONE tab takes the lead to call the Backend API `/logout`.
 * The other tabs will just wait and be torn down locally by the Broadcast message.
 */

const INACTIVITY_LOGOUT_LOCK = 'oir_inactivity_logout';

/**
 * THE BOOTSTRAP PROCESS (App Startup / F5 / Hard Refresh)
 * This runs exactly once when the app starts. It tries to silently get a new access token
 * using the HttpOnly refresh cookie, and loads the user's profile before showing the UI.
 *
 * STEP 1: SERIALIZED REFRESH (The Queue)
 * If the user opens 5 tabs at once, they would all hit the backend simultaneously with the
 * same refresh cookie. The backend would think it's a replay attack, blacklist the cookie,
 * and log the user out permanently.
 * To fix this, `bootstrapRefreshToken` uses a Web Lock (`oir_token_refresh`). The tabs
 * line up. The first one gets the new token from the server and shouts it on the Walkie-Talkie
 * (`TOKEN_REFRESHED`). The other tabs hear it while waiting in line, copy it, and skip the server.
 *
 * STEP 2: LOAD ROLES BEFORE UI (The Order)
 * Because the access token only lives in memory, an F5 clears it. It also clears the user's Roles.
 * If we open the gates (`bootstrapStatus = 'authenticated'`) BEFORE fetching the roles again,
 * a user refreshing on a restricted page (like User Management) will be instantly kicked out
 * to `/unauthorized` because their roles array is empty for a split second.
 * So, we MUST wait for the profile fetch (`getUserByEmail`) to finish and populate the roles
 * BEFORE we finally set the status to 'authenticated'.
 *
 * STEP 3: SILENT CREDENTIALS
 * We wrap `setCredentials` in `skipBroadcast`. This tab is just waking up and remembering
 * who it is; it shouldn't yell 'LOGIN' to the other tabs, because they are already awake
 * and would needlessly reset their cache.
 *
 * STEP 4: FAILURE MODES
 * - 401 / Invalid Cookie -> 'anonymous' (Go to Login screen).
 * - Server offline / Timeout -> 'unreachable' (Show a "Retry" screen so we don't accidentally
 * log out a valid user just because the Wi-Fi blinked).
 */

export function bootstrapAuth(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const outcome = await bootstrapRefreshToken({ dispatch, getState });

    if (outcome.status === RefreshStatus.UNREACHABLE) {
      dispatch(setBootstrapStatus('unreachable'));
      return;
    }

    if (outcome.status !== RefreshStatus.REFRESHED) {
      dispatch(setBootstrapStatus('anonymous'));
      return;
    }

    try {
      const { token } = outcome;
      const username = getEmailFromAccessToken(token);

      await dispatch(
        userManagementApiSlice.endpoints.getUserByEmail.initiate(username, { subscribe: false, forceRefetch: true }),
      ).unwrap();

      dispatch(skipBroadcast(setCredentials({ username, token })));
    } catch (error) {
      dispatch(setBootstrapStatus(isUnreachableError(error) ? 'unreachable' : 'anonymous'));
    }
  };
}

export function invokeLogout(): AppThunk<Promise<void>> {
  return async (dispatch) => {
    // 1. Cancel any refresh requests that are currently flying through the network.
    // If we don't, a delayed refresh response might arrive AFTER we logged out and resurrect the session.
    cancelInFlightRefresh();

    // 2. Tell the backend to release the tokens held by this specific tab.
    // We MUST `await` this before `logout()` runs, because this request needs the Access Token
    // which is about to be deleted from memory.
    await cancelTrackedReservation(dispatch);

    // 3. Notify the backend that the session is over, then clear local state safely.
    try {
      await dispatch(authenticationApiSlice.endpoints.logout.initiate()).unwrap();
    } catch (error) {
      console.error('Logout request failed', error);
    } finally {
      // 4. Drop the shared inactivity clock so the next session starts from a clean slate.
      clearActivity();

      // 5. Purge all Redux data (cache, user state, etc.)
      dispatch(resetAllApiState());
      dispatch(logout());
    }
  };
}

/**
 * Logs the user out when the Inactivity Timer hits 0.
 *
 * Why the Web Lock?
 * Since all open tabs share the same clock, they will all hit 0 simultaneously.
 * We use `ifAvailable: true` to pick a "Leader".
 * - The single tab that grabs the lock will call the backend to log out.
 * - The tabs that arrive a millisecond late will see `lock: null`, realize
 * someone else is handling it, and quietly do nothing (they will be logged out
 * locally by the Walkie-Talkie message anyway).
 *
 * Note: The inner check `logoutIfStillAuthenticated` handles the case where a tab
 * grabs the lock just a split second AFTER another tab already wiped the session.
 */

export function invokeInactivityLogout(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const executeLogout = async (): Promise<void> => {
      if (selectIsAuthenticated(getState())) {
        await dispatch(invokeLogout());
      }
    };

    if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
      await executeLogout();
      return;
    }

    await navigator.locks.request(INACTIVITY_LOGOUT_LOCK, { ifAvailable: true }, async (lock) => {
      if (lock) {
        await executeLogout();
      }
    });
  };
}
